
// Added React to imports to fix namespace errors
import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { COURSES } from '../data/courses';
import { db } from '../firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp, limit, doc, updateDoc, increment, getDocs, Timestamp,
  QuerySnapshot, DocumentData, Query, setDoc, deleteDoc, or, getDoc, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { Card, Button, Badge, Input } from '../components/ui';
import { ChatMessage, ForumReport, Connection } from '../types';
import { validateNickname } from '../utils/moderation';
import { useNavigate, useLocation } from 'react-router-dom';
import { SystemCheckFlow, SystemCheckinData } from '../components/SystemCheckFlow';
import { SystemDiagnostic } from '../components/SystemDiagnostic';

const SITUATIONS = [
  { id: 'court', label: 'In Family Court', icon: '🏛️' },
  { id: 'allegations', label: 'False Allegations', icon: '🛡️' },
  { id: 'contact', label: 'Contact Blocked', icon: '🚫' },
  { id: 'safeguarding', label: 'Safeguarding Issues', icon: '🚩' },
  { id: 'alienation', label: 'Parental Alienation', icon: '💔' },
  { id: 'mediation', label: 'In Mediation/MIAM', icon: '🤝' },
];

const FORUM_CHANNELS = [
  { id: 'general', title: 'General Support', description: 'Introduce yourself and share your overall journey.', icon: '🤝' },
  { id: 'legal_strategy', title: 'Legal Strategy & Orders', description: 'Position statements, hearings, and court orders.', icon: '⚖️' },
  { id: 'cafcass_prep', title: 'Cafcass & Social Services', description: 'Preparing for Section 7 reports and interviews.', icon: '📋' },
  { id: 'vent_space', title: 'The Vent Space', description: 'A safe place to let out frustration anonymously.', icon: '🌋' },
  { id: 'stronger_together', title: 'Stronger Together', description: 'Share your system check-ins and support other dads.', icon: '🛡️' },
  { id: 'success_stories', title: 'Success Stories', description: 'Share your wins, big or small! Inspire others.', icon: '🏆' },
];

const MOODS = [
  { id: 'advice', label: 'Need Advice', emoji: '🤔', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { id: 'rant', label: 'Just Venting', emoji: '😤', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { id: 'success', label: 'Victory', emoji: '🎉', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { id: 'urgent', label: 'Urgent Help', emoji: '🚨', color: 'bg-red-100 text-red-800 font-bold border-red-300' },
];
// Utility for rendering mentions and hashtags beautifully
const FormattedText = ({ text }: { text: string }) => {
  // Sync linter
  if (!text) return null;
  const parts = text.split(/(@\w+|#\w+)/g);
  return (
    <span className="whitespace-pre-wrap leading-relaxed">
      {parts.map((part, i) => {
        if (part.startsWith('@')) {
          return <span key={i} className="text-md-primary font-bold cursor-pointer hover:underline">{part}</span>;
        } else if (part.startsWith('#')) {
          return <span key={i} className="text-secondary font-bold cursor-pointer hover:underline text-indigo-500">{part}</span>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

const AVAILABLE_REACTIONS = [
  { emoji: '👍', label: 'Like' },
  { emoji: '❤️', label: 'Love' },
  { emoji: '💪', label: 'Stay Strong' },
  { emoji: '🔥', label: 'Hot Topic' }
];

const ReactionBar = ({ message, onReact, uid, className = 'mt-3' }: { message: ChatMessage, onReact: (msgId: string, emoji: string, hasReacted: boolean) => void, uid: string, className?: string }) => {
  return (
    <div className={`flex gap-2 items-center flex-wrap relative z-20 ${className}`}>
      {AVAILABLE_REACTIONS.map(({ emoji, label }) => {
        const reacts = message.reactions?.[emoji] || [];
        const count = reacts.length;
        const hasReacted = reacts.includes(uid);
        return (
          <button 
            key={emoji}
            title={label}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onReact(message.id, emoji, hasReacted); }}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] md:text-xs font-bold transition-all border ${
              hasReacted ? 'bg-md-primary/10 text-md-primary border-md-primary/20 hover:bg-md-primary/20' : 'bg-transparent text-md-on-surface-variant hover:bg-md-surface-container border-md-outline-variant/30 hover:border-md-outline-variant/50'
            }`}
          >
            <span className="leading-none transform translate-y-[1px]">{emoji}</span>
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

const Forum = () => {
  const { userProfile, refreshProfile, schoolProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCourseId, setSelectedCourseId] = useState(FORUM_CHANNELS[0].id);
  const [newThreadMood, setNewThreadMood] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [indexErrorUrl, setIndexErrorUrl] = useState<string | null>(null);

  const [quickReplyText, setQuickReplyText] = useState<{ [threadId: string]: string }>({});
  const [isQuickReplying, setIsQuickReplying] = useState<{ [threadId: string]: boolean }>({});
  const [activeQuickReply, setActiveQuickReply] = useState<string | null>(null);

  const [showThermometer, setShowThermometer] = useState(false);
  const [showIntervention, setShowIntervention] = useState(false);
  const [latestCheckinData, setLatestCheckinData] = useState<SystemCheckinData | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string, isThread: boolean } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const threadParam = params.get('thread');
    if (threadParam) setActiveThreadId(threadParam);

    const channelParam = params.get('channel');
    if (channelParam) {
        // Validate it's a real channel
        if (FORUM_CHANNELS.some(c => c.id === channelParam)) {
            setSelectedCourseId(channelParam);
        }
    }

    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [location.search]);

  const [editingThread, setEditingThread] = useState<ChatMessage | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [mentionSearch, setMentionSearch] = useState<{ active: boolean, text: string, index: number } | null>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const initialThreadScores = useRef<Record<string, number>>({});
  const initialReplyScores = useRef<Record<string, number>>({});

  const [showTopicsMobile, setShowTopicsMobile] = useState(false);
  const [showUsersMobile, setShowUsersMobile] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const [pendingNickname, setPendingNickname] = useState('');
  const [selectedSituations, setSelectedSituations] = useState<string[]>([]);
  const [isValidatingNickname, setIsValidatingNickname] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);

  const [reportPost, setReportPost] = useState<ChatMessage | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const [viewingProfile, setViewingProfile] = useState<{ name: string, uid: string, role: string, isTeam: boolean } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'none' | 'pending' | 'accepted'>('none');
  const [isConnecting, setIsConnecting] = useState(false);

  const isAdmin = userProfile?.role === 'admin';
  const isPaid = userProfile?.isPaid || schoolProfile?.isPaid || isAdmin || userProfile?.schoolId === 'dadsmatter';
  const isStaff = isAdmin || userProfile?.role === 'staff' || userProfile?.role === 'manager';

  const [prosPerRoom, setProsPerRoom] = useState<Record<string, string[]>>({});
  const [usersInCurrentRoom, setUsersInCurrentRoom] = useState<Array<{ name: string, uid: string, role: string, isTeam: boolean }>>([]);
  const [allPresence, setAllPresence] = useState<Record<string, boolean>>({});
  const [globalUsernames, setGlobalUsernames] = useState<string[]>([]);

  const selectedCourse = FORUM_CHANNELS.find(c => c.id === selectedCourseId) || FORUM_CHANNELS[0];

  useEffect(() => {
    const fetchGlobalUsers = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const names = snap.docs.map(d => d.data().nickname || d.data().displayName).filter(Boolean);
        setGlobalUsernames(names);
      } catch (e) {
        console.error("Error fetching global users for mentions:", e);
      }
    };
    fetchGlobalUsers();
  }, []);

  useEffect(() => {
    initialReplyScores.current = {};
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [activeThreadId]);

  useEffect(() => {
    initialThreadScores.current = {};
    initialReplyScores.current = {};
  }, [selectedCourseId]);

  useEffect(() => {
    if (!viewingProfile || !userProfile) return;

    const checkConnection = async () => {
      const q = query(
        collection(db, 'connections'),
        where('users', 'array-contains', userProfile.uid)
      );
      const snap = await getDocs(q);
      const conn = snap.docs.find(d => {
        const data = d.data();
        return data.users.includes(viewingProfile.uid);
      });

      if (conn) {
        setConnectionStatus(conn.data().status);
      } else {
        setConnectionStatus('none');
      }
    };

    checkConnection();
  }, [viewingProfile, userProfile]);

  const handleRequestConnection = async () => {
    if (!viewingProfile || !userProfile || !isPaid) {
      if (!isPaid) navigate('/subscription');
      return;
    }

    setIsConnecting(true);
    try {
      // If admin, we force accepted status immediately
      const targetStatus = isAdmin ? 'accepted' : 'pending';

      // Check if connection already exists (even if pending)
      const q = query(
        collection(db, 'connections'),
        where('users', 'array-contains', userProfile.uid)
      );
      const snap = await getDocs(q);
      const existingConn = snap.docs.find(d => d.data().users.includes(viewingProfile.uid));

      if (existingConn) {
        if (isAdmin && existingConn.data().status !== 'accepted') {
          await updateDoc(existingConn.ref, { status: 'accepted', updatedAt: serverTimestamp() });
        }
        navigate('/messages');
        return;
      }

      const connectionData = {
        users: [userProfile.uid, viewingProfile.uid],
        requesterUid: userProfile.uid,
        receiverUid: viewingProfile.uid,
        status: targetStatus,
        updatedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'connections'), connectionData);

      if (isAdmin) {
        navigate('/messages');
      } else {
        setConnectionStatus('pending');
        alert("Connection request sent!");
      }
    } catch (err) {
      console.error("Connection failed:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  useEffect(() => {
    if (!userProfile?.nickname) return;

    const presenceDocRef = doc(db, 'presence', userProfile.uid);

    // If online hidden, we shouldn't even set presence in the room
    if (userProfile.isOnlineHidden) return;

    const updatePresence = () => {
      setDoc(presenceDocRef, {
        uid: userProfile.uid,
        role: userProfile.role,
        isTeamMember: userProfile.schoolId === 'dadsmatter',
        name: userProfile.nickname,
        currentRoomId: selectedCourseId,
        lastActive: serverTimestamp()
      }, { merge: true }).catch(() => { });
    };

    updatePresence();
    const heartbeat = setInterval(updatePresence, 60000);

    const handleUnload = () => {
      deleteDoc(presenceDocRef).catch(() => { });
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleUnload);
      deleteDoc(presenceDocRef).catch(() => { });
    };
  }, [userProfile, selectedCourseId]);

  useEffect(() => {
    const q = query(collection(db, 'presence'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const proRoomMap: Record<string, string[]> = {};
      const currentRoomParticipants: Array<{ name: string, uid: string, role: string, isTeam: boolean }> = [];
      const activeUids: Record<string, boolean> = {};
      const now = Date.now();

      snapshot.forEach(doc => {
        const data = doc.data();

        // Ignore stale presence older than 2.5 minutes (150,000ms), assume Date.now() if pending writes (null)
        const lastActiveMs = data.lastActive?.toMillis ? data.lastActive.toMillis() : now;
        if (now - lastActiveMs > 150000) return;

        activeUids[data.uid] = true;
        const isPro = ['staff', 'manager', 'admin'].includes(data.role) || data.isTeamMember === true;

        if (isPro && data.currentRoomId) {
          if (!proRoomMap[data.currentRoomId]) proRoomMap[data.currentRoomId] = [];
          if (!proRoomMap[data.currentRoomId].includes(data.name)) {
            proRoomMap[data.currentRoomId].push(data.name);
          }
        }

        if (data.currentRoomId === selectedCourseId) {
          currentRoomParticipants.push({
            uid: data.uid,
            name: data.name,
            role: data.role,
            isTeam: data.isTeamMember || false
          });
        }
      });

      setProsPerRoom(proRoomMap);
      setUsersInCurrentRoom(currentRoomParticipants.sort((a, b) => a.name.localeCompare(b.name)));
      setAllPresence(activeUids);
    });

    return () => unsubscribe();
  }, [selectedCourseId]);

  useEffect(() => {
    if (!selectedCourseId || !userProfile?.nickname) return;

    let q: Query<DocumentData>;
    const baseCol = collection(db, 'forum_messages');

    try {
      q = query(
        baseCol,
        where('courseId', '==', selectedCourseId),
        where('status', 'in', ['safe', 'pending']),
        orderBy('createdAt', 'asc'),
        limit(500)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const msgs: ChatMessage[] = [];
        snapshot.docs.forEach((doc) => {
          msgs.push({ id: doc.id, ...doc.data() } as ChatMessage);
        });
        setMessages(msgs);
        setIndexErrorUrl(null);
      }, (err: any) => {
        console.error("Firestore Snapshot Error:", err);
        if (err.message?.includes('index')) {
          const match = err.message.match(/https:\/\/console\.firebase\.google\.com[^\s]*/);
          if (match) setIndexErrorUrl(match[0]);
        }
      });

      return () => unsubscribe();
    } catch (err: any) {
      console.error("Query Construction Error:", err);
    }
  }, [selectedCourseId, userProfile?.nickname]);

  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages;
    const q = searchQuery.toLowerCase();
    return messages.filter(m => {
      const matchText = m.text?.toLowerCase().includes(q);
      const matchTitle = m.title?.toLowerCase().includes(q);
      const matchUser = m.userName?.toLowerCase().includes(q);
      return matchText || matchTitle || matchUser;
    });
  }, [messages, searchQuery]);

  // 1. Compute Replies (has parentId) mapping FIRST
  const repliesByThread = useMemo(() => {
    const map: Record<string, ChatMessage[]> = {};
    filteredMessages.forEach(m => {
      if (m.parentId) {
         if (!map[m.parentId]) map[m.parentId] = [];
         map[m.parentId].push(m);
      }
    });
    // Sort replies using the community gravity algorithm
    Object.keys(map).forEach(threadId => {
      map[threadId].sort((a, b) => {
        let gravityA = initialReplyScores.current[a.id];
        if (gravityA === undefined) {
            const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : Date.now();
            const reactionsA = Object.values(a.reactions || {}).reduce((acc, rs) => acc + rs.length, 0);
            const scoreA = (reactionsA * 2);
            const daysOldA = Math.max(0, (Date.now() - timeA) / 86400000);
            gravityA = scoreA / (1 + daysOldA);
            initialReplyScores.current[a.id] = gravityA;
        }

        let gravityB = initialReplyScores.current[b.id];
        if (gravityB === undefined) {
            const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : Date.now();
            const reactionsB = Object.values(b.reactions || {}).reduce((acc, rs) => acc + rs.length, 0);
            const scoreB = (reactionsB * 2);
            const daysOldB = Math.max(0, (Date.now() - timeB) / 86400000);
            gravityB = scoreB / (1 + daysOldB);
            initialReplyScores.current[b.id] = gravityB;
        }
        
        if (Math.abs(gravityB - gravityA) > 0.01) {
            return gravityB - gravityA;
        }

        // Fallback to reading order (oldest first) if no engagement differences
        const fbTimeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : Date.now();
        const fbTimeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : Date.now();
        return fbTimeA - fbTimeB;
      });
    });

    return map;
  }, [filteredMessages]);

  // 2. Compute Threads (no parentId)
  const threads = useMemo(() => {
    let result: ChatMessage[] = [];
    if (!searchQuery.trim()) {
       result = filteredMessages.filter(m => !m.parentId);
    } else {
       const matchingThreadIds = new Set<string>();
       filteredMessages.forEach(m => {
         if (!m.parentId) matchingThreadIds.add(m.id);
         else matchingThreadIds.add(m.parentId);
       });
       result = messages.filter(m => !m.parentId && matchingThreadIds.has(m.id));
    }
    
    return result.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      
      let gravityA = initialThreadScores.current[a.id];
      if (gravityA === undefined) {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : Date.now();
          const viewsA = a.views || 0;
          const repliesA = (repliesByThread[a.id] || []).length;
          const reactionsA = Object.values(a.reactions || {}).reduce((acc, rs) => acc + rs.length, 0);
          const scoreA = viewsA + (repliesA * 5) + (reactionsA * 2);
          const daysOldA = Math.max(0, (Date.now() - timeA) / 86400000);
          gravityA = scoreA / (1 + daysOldA);
          initialThreadScores.current[a.id] = gravityA;
      }

      let gravityB = initialThreadScores.current[b.id];
      if (gravityB === undefined) {
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : Date.now();
          const viewsB = b.views || 0;
          const repliesB = (repliesByThread[b.id] || []).length;
          const reactionsB = Object.values(b.reactions || {}).reduce((acc, rs) => acc + rs.length, 0);
          const scoreB = viewsB + (repliesB * 5) + (reactionsB * 2);
          const daysOldB = Math.max(0, (Date.now() - timeB) / 86400000);
          gravityB = scoreB / (1 + daysOldB);
          initialThreadScores.current[b.id] = gravityB;
      }
      
      if (Math.abs(gravityB - gravityA) > 0.01) {
          return gravityB - gravityA;
      }

      const fbTimeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : Date.now();
      const fbTimeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : Date.now();
      return fbTimeB - fbTimeA;
    });
  }, [filteredMessages, messages, searchQuery, repliesByThread]);

  const activeThread = useMemo(() => {
     if (!activeThreadId) return null;
     return threads.find(t => t.id === activeThreadId) || null;
  }, [activeThreadId, threads]);

  const handleNicknameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingNickname.trim() || !userProfile || isValidatingNickname) return;
    if (selectedSituations.length === 0) {
      setNicknameError("Please select at least one situation that matches your journey.");
      return;
    }

    setNicknameError(null);
    setIsValidatingNickname(true);
    try {
      const validation = await validateNickname(pendingNickname, userProfile.displayName);
      if (!validation.isValid) {
        setNicknameError(validation.error || "Please choose a different nickname.");
        setIsValidatingNickname(false);
        return;
      }
      const userRef = doc(db, 'users', userProfile.uid);
      await updateDoc(userRef, {
        nickname: pendingNickname.trim(),
        situations: selectedSituations
      });
      await refreshProfile();
    } catch (err) {
      setNicknameError("An error occurred. Please try again.");
    } finally {
      setIsValidatingNickname(false);
    }
  };

  const toggleSituation = (id: string) => {
    setSelectedSituations(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const uniqueUserNames = useMemo(() => {
     const names = new Set<string>();
     messages.forEach(m => names.add(m.userName));
     usersInCurrentRoom.forEach(u => names.add(u.name));
     globalUsernames.forEach(n => names.add(n));
     
     // Remove current user from auto-complete suggestions
     const myName = userProfile?.nickname || userProfile?.displayName;
     if (myName) names.delete(myName);
     
     return Array.from(names).filter(Boolean);
  }, [messages, usersInCurrentRoom, globalUsernames, userProfile]);

  const mentionMatches = useMemo(() => {
     if (!mentionSearch?.active) return [];
     const q = mentionSearch.text.toLowerCase();
     if (!q) return uniqueUserNames.slice(0, 6);
     return uniqueUserNames.filter(n => n.toLowerCase().includes(q) && n.length > 0).slice(0, 6);
  }, [mentionSearch, uniqueUserNames]);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNewMessage(val);
    const cursor = e.target.selectionStart;
    setCursorPosition(cursor);
    
    // Look backward from cursor to find '@'
    const textBeforeCursor = val.substring(0, cursor);
    const match = textBeforeCursor.match(/@([a-zA-Z0-9_]*)$/);
    
    if (match) {
      setMentionSearch({ active: true, text: match[1], index: match.index! });
    } else {
      setMentionSearch(null);
    }
  };

  const insertMention = (name: string) => {
    if (!mentionSearch) return;
    const before = newMessage.substring(0, mentionSearch.index);
    const after = newMessage.substring(cursorPosition);
    setNewMessage(`${before}@${name} ${after}`);
    setMentionSearch(null);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !userProfile || isSending) return;
    
    setIsSending(true);
    try {
      const parentId = activeThreadId || null;
      const ref = await addDoc(collection(db, 'forum_messages'), {
        courseId: selectedCourseId,
        userId: userProfile.uid,
        userName: userProfile.nickname || userProfile.displayName,
        text: newMessage,
        createdAt: serverTimestamp(),
        status: 'safe',
        reportCount: 0,
        parentId, // Null if starting a new thread
        mood: (!activeThreadId && newThreadMood) ? newThreadMood : null
      });

      // Extract unique mentions and create notifications
      const matches = newMessage.match(/@([a-zA-Z0-9_]+)/g);
      if (matches) {
        const uniqueMentions = Array.from(new Set(matches.map(m => m.substring(1))));
        const myName = userProfile.nickname || userProfile.displayName;
        
        uniqueMentions.forEach(async (mentionName) => {
          if (mentionName === myName) return; // Don't notify self
          try {
            await addDoc(collection(db, 'notifications'), {
              type: 'mention',
              recipientName: mentionName,
              senderId: userProfile.uid,
              senderName: myName,
              threadId: parentId || ref.id,
              text: newMessage,
              createdAt: serverTimestamp(),
              read: false
            });
          } catch (err) {
            console.error("Error creating notification", err);
          }
        });
      }

      setNewMessage('');
      setMentionSearch(null);
      if (!activeThreadId) {
        setNewThreadMood('');
        setIsCreatingThread(false);
      }
    } catch (error) {
      console.error("Error posting to forum:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickReply = async (e: React.FormEvent, threadId: string) => {
      e.preventDefault();
      const text = quickReplyText[threadId];
      if (!text?.trim() || !userProfile || !currentUser || isQuickReplying[threadId]) return;
      
      setIsQuickReplying(prev => ({ ...prev, [threadId]: true }));
      try {
          const ref = await addDoc(collection(db, 'forum_messages'), {
              courseId: selectedCourseId,
              userId: userProfile.uid,
              userName: userProfile.nickname || userProfile.displayName,
              text,
              createdAt: serverTimestamp(),
              status: 'safe',
              reportCount: 0,
              parentId: threadId,
              mood: null
          });

          const matches = text.match(/@([a-zA-Z0-9_]+)/g);
          if (matches) {
              const uniqueMentions = Array.from(new Set(matches.map(m => m.substring(1))));
              const myName = userProfile.nickname || userProfile.displayName;
              uniqueMentions.forEach(async (mentionName) => {
                  if (mentionName === myName) return;
                  await addDoc(collection(db, 'notifications'), {
                      type: 'mention', recipientName: mentionName, senderId: userProfile.uid,
                      senderName: myName, threadId: threadId, text: text,
                      createdAt: serverTimestamp(), read: false
                  }).catch(console.error);
              });
          }

          setQuickReplyText(prev => ({ ...prev, [threadId]: '' }));
          setActiveQuickReply(null);
      } catch (err) {
          console.error("Error Quick Replying:", err);
      } finally {
          setIsQuickReplying(prev => ({ ...prev, [threadId]: false }));
      }
  };

  const handleViewThread = async (threadId: string) => {
      setActiveThreadId(threadId);
      try {
          const threadRef = doc(db, 'forum_messages', threadId);
          await updateDoc(threadRef, { views: increment(1) });
      } catch (err) {
          console.error("View increment failed:", err);
      }
  };

  const handleDeletePost = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      if (deleteConfirm.isThread) {
        const repliesQuery = query(collection(db, 'forum_messages'), where('parentId', '==', deleteConfirm.id));
        const repliesSnap = await getDocs(repliesQuery);
        const deletePromises = repliesSnap.docs.map(d => deleteDoc(d.ref));
        await Promise.all(deletePromises);
      }
      await deleteDoc(doc(db, 'forum_messages', deleteConfirm.id));
      if (deleteConfirm.isThread && activeThreadId === deleteConfirm.id) {
        setActiveThreadId(null);
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSystemCheckComplete = async (data: SystemCheckinData) => {
      if (!userProfile) return;
      try {
          const userRef = doc(db, 'users', userProfile.uid);
          await updateDoc(userRef, {
              thermalCheckins: arrayUnion({
                  id: Date.now().toString(),
                  timestamp: new Date().toISOString(),
                  ...data
              })
          });
          await refreshProfile();
          setLatestCheckinData(data);
          setShowThermometer(false);
          setShowIntervention(true);
      } catch (error) {
          console.error('Error saving system checkin:', error);
      }
  };

  const handleReaction = async (messageId: string, emoji: string, hasReacted: boolean) => {
      if (!userProfile) return;
      try {
          const fieldPath = `reactions.${emoji}`;
          await updateDoc(doc(db, 'forum_messages', messageId), {
              [fieldPath]: hasReacted ? arrayRemove(userProfile.uid) : arrayUnion(userProfile.uid)
          });
      } catch (err) {
          console.error("Reaction failed:", err);
      }
  };

  const handlePinThread = async (messageId: string, currentPinStatus: boolean) => {
      if (!isStaff) return;
      if (!currentPinStatus) {
         const pinnedCount = threads.filter(t => t.isPinned).length;
         if (pinnedCount >= 3) {
            alert("Maximum of 3 threads can be pinned. Please unpin a thread first.");
            return;
         }
      }
      try {
          await updateDoc(doc(db, 'forum_messages', messageId), {
              isPinned: !currentPinStatus
          });
      } catch (err) {
          console.error("Pin failed:", err);
      }
  };

  const handleShare = async (threadId: string, title?: string) => {
    const url = `${window.location.origin}/forum?thread=${threadId}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: title ? `Dadshub: ${title}` : 'Dadshub Discussion',
          text: 'Check out this discussion on Dadshub!',
          url: url,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy link:", err);
      }
    }
  };

  const openEditModal = (thread: ChatMessage) => {
    setEditingThread(thread);
    setEditTitle(thread.title || '');
    setEditText(thread.text || '');
    setActiveDropdown(null);
  };

  const handleSaveEdit = async () => {
    if (!editingThread || !editText.trim()) return;
    setIsSavingEdit(true);
    try {
      await updateDoc(doc(db, 'forum_messages', editingThread.id), {
        title: editTitle,
        text: editText,
      });
      setEditingThread(null);
    } catch (err) {
      console.error("Edit failed:", err);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportPost || !reportReason.trim() || !userProfile) return;
    setIsSubmittingReport(true);
    try {
      const report: ForumReport = {
        postId: reportPost.id,
        postContent: reportPost.text,
        reportedUserId: reportPost.userId,
        reportedUserName: reportPost.userName,
        reporterId: userProfile.uid,
        reporterName: userProfile.nickname || userProfile.displayName,
        reason: reportReason,
        courseId: selectedCourseId,
        createdAt: serverTimestamp(),
        status: 'pending'
      };
      await addDoc(collection(db, 'forum_reports'), report);
      await updateDoc(doc(db, 'forum_messages', reportPost.id), { reportCount: increment(1) });
      setReportPost(null);
      setReportReason('');
      alert("Reported successfully.");
    } catch (err) {
      console.error("Reporting failed:", err);
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (userProfile && !userProfile.nickname) {
    return (
      <Layout>
        <div className="min-h-full flex items-center justify-center animate-fade-in p-4 md:p-8">
          <Card className="w-full max-w-4xl shadow-2xl border-0 ring-1 ring-slate-900/5 overflow-hidden">
            <div className="md:flex">
              <div className="md:w-4/12 bg-indigo-600 p-8 text-white">
                <h3 className="text-xl font-heading font-bold mb-6 flex items-center gap-2">
                  <span>⚖️</span> Dads Hub Rules
                </h3>
                <div className="space-y-6">
                  <div className="space-y-1">
                    <p className="text-xs font-black uppercase tracking-widest text-indigo-200">The Purpose</p>
                    <p className="text-sm leading-relaxed text-indigo-50">Support, information, and shared advice for the family court journey.</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-black uppercase tracking-widest text-indigo-200">The Focus</p>
                    <p className="text-sm leading-relaxed text-indigo-50">Every discussion must remain <strong>child-focused</strong>.</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-black uppercase tracking-widest text-indigo-200">Strict Conduct</p>
                    <p className="text-sm leading-relaxed text-indigo-50 italic">No defamation or "mud slinging."</p>
                  </div>
                </div>
                <div className="mt-12 pt-12 border-t border-indigo-500/30">
                  <p className="text-[10px] uppercase font-bold text-indigo-300 tracking-[0.2em]">Safety Notice</p>
                  <p className="text-[11px] text-indigo-100/60 mt-2 italic">You are entering a secure, moderated peer environment.</p>
                </div>
              </div>

              <div className="md:w-8/12 p-8 md:p-12 flex flex-col justify-center bg-white overflow-y-auto">
                <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Secure Entry</h2>
                <p className="text-slate-500 text-sm mb-10">Setup your anonymous profile to join the discussion.</p>

                {nicknameError && <div className="mb-6 p-4 bg-rose-50 text-rose-600 text-xs font-bold rounded-xl border border-rose-100 animate-shake">{nicknameError}</div>}

                <form onSubmit={handleNicknameSubmit} className="space-y-10">
                  <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">1. What describes your situation? (Tick all that apply)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SITUATIONS.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleSituation(s.id)}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${selectedSituations.includes(s.id) ? 'border-indigo-600 bg-indigo-50 text-indigo-900 shadow-md ring-4 ring-indigo-50' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200'}`}
                        >
                          <span className="text-xl">{s.icon}</span>
                          <span className="text-xs font-bold">{s.label}</span>
                          {selectedSituations.includes(s.id) && (
                            <span className="ml-auto text-indigo-600 font-bold">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">2. Choose a forum name</p>
                    <Input
                      label="Dads Hub Nickname"
                      placeholder="e.g. BraveDads2026..."
                      value={pendingNickname}
                      onChange={(e) => setPendingNickname(e.target.value)}
                      required
                      disabled={isValidatingNickname}
                      maxLength={20}
                      className="mb-0"
                    />
                    <p className="text-[10px] text-slate-400 mt-2 italic px-1">Tip: Do not use your real name to remain anonymous.</p>
                  </div>

                  <Button type="submit" className="w-full py-4 text-sm !rounded-xl shadow-xl shadow-indigo-500/20" disabled={isValidatingNickname || !pendingNickname.trim() || selectedSituations.length === 0}>
                    {isValidatingNickname ? "Verifying..." : "Accept Rules & Enter Hub"}
                  </Button>
                </form>
              </div>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout fluid>
      <div className="flex flex-col h-full bg-md-surface-container relative overflow-hidden">
        {/* Mobile Header Tabs */}
        <div className="md:hidden flex-shrink-0 flex gap-px bg-md-surface border-b border-md-outline-variant/30 shadow-sm sticky top-0 z-40">
          <button
            onClick={() => { setShowTopicsMobile(true); setShowUsersMobile(false); }}
            className="flex-1 px-4 py-3 bg-md-surface-container-lowest text-md-on-surface text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 active:bg-md-surface-container"
          >
            <span>📁</span> Channels
          </button>
          <button
            onClick={() => { setShowUsersMobile(true); setShowTopicsMobile(false); }}
            className="flex-1 px-4 py-3 bg-md-surface-container-lowest text-md-on-surface text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 active:bg-md-surface-container"
          >
            <span>📜</span> Guidelines
          </button>
        </div>

        {/* Global Toolbar (Desktop) */}
        <div className="hidden md:flex flex-shrink-0 p-4 lg:p-6 pb-2 justify-between items-end gap-4 bg-md-surface border-b border-md-outline-variant/30">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-black text-md-on-surface truncate tracking-tighter">Dads Hub</h2>
              <Badge color="yellow">BETA</Badge>
            </div>
            <p className="text-md-on-surface-variant text-[10px] md:text-xs font-medium">Child-focused peer support for the family court journey.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-md-on-surface-variant text-sm">🔍</span>
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-md-surface-container-lowest border border-md-outline-variant/50 rounded-full text-sm font-medium focus:ring-2 focus:ring-md-primary/30 transition-all w-64 shadow-inner text-md-on-surface outline-none"
              />
            </div>
            <div className="flex items-center gap-2 bg-md-secondary-container px-3 py-1.5 rounded-full border border-md-secondary-container/50 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse"></span>
              <span className="text-[10px] font-bold text-md-on-secondary-container">@{userProfile?.nickname}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-row gap-0 overflow-hidden min-h-0 bg-md-surface-container">
          <aside className={`
            fixed inset-y-0 left-0 z-[60] w-72 bg-md-surface-container-low text-md-on-surface transform transition-transform duration-300 flex flex-col h-full shadow-2xl
            md:relative md:translate-x-0 md:z-auto md:shadow-none border-r border-md-outline-variant/30 flex-shrink-0
            ${showTopicsMobile ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}>
            <div className="p-5 flex-shrink-0 flex justify-between items-center border-b border-md-outline-variant/30 bg-md-surface-container-lowest">
              <p className="text-md-on-surface-variant font-black text-[10px] uppercase tracking-[0.2em] drop-shadow-sm">Discussion Channels</p>
              <button onClick={() => setShowTopicsMobile(false)} className="md:hidden text-md-on-surface-variant hover:text-md-on-surface p-1">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
              {FORUM_CHANNELS.map(course => {
                const activePros = prosPerRoom[course.id] || [];
                const hasPros = activePros.length > 0;
                const isActive = selectedCourseId === course.id;

                return (
                  <button
                    key={course.id}
                    onClick={() => { setSelectedCourseId(course.id); setActiveThreadId(null); setShowTopicsMobile(false); }}
                    className={`w-full flex justify-between items-center px-4 py-3 rounded-2xl transition-all text-left group border ${isActive ? 'bg-md-primary-container border-md-primary/20 shadow-sm text-md-on-primary-container' : 'bg-transparent border-transparent hover:bg-md-surface-container hover:border-md-outline-variant/30 text-md-on-surface'}`}
                  >
                    <div className="flex items-center gap-3 truncate">
                      <span className="text-xl flex-shrink-0 drop-shadow-sm">{course.icon}</span>
                      <span className={`text-[13px] font-bold truncate tracking-tight ${isActive ? 'text-md-on-primary-container' : 'text-md-on-surface-variant group-hover:text-md-on-surface'}`}>{course.title}</span>
                    </div>
                    {hasPros && (
                      <div className="flex items-center gap-1.5 bg-md-tertiary-container px-2 py-0.5 rounded-full border border-md-tertiary/20 flex-shrink-0 shadow-sm animate-fade-in">
                        <span className="w-1.5 h-1.5 rounded-full bg-md-on-tertiary-container animate-pulse"></span>
                        <span className="text-[9px] font-black uppercase tracking-tighter text-md-on-tertiary-container">PRO</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Create Post Button (Mobile Anchor) */}
            <div className="p-4 border-t border-md-outline-variant/30 bg-md-surface-container-lowest md:hidden">
              <Button 
                onClick={() => { setActiveThreadId(null); setShowTopicsMobile(false); if(scrollRef.current) scrollRef.current.scrollTop = 0; }}
                className="w-full !rounded-2xl py-3.5 shadow-md flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <span className="text-lg">✍️</span> New Post
              </Button>
            </div>
          </aside>

          <div className="flex-1 flex flex-col min-h-0 bg-md-surface-container-lowest relative">
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto bg-md-surface-container-lowest custom-scrollbar scroll-smooth"
            >
              {/* Feed Header */}
              {!activeThreadId && (
                <div className="bg-md-surface/90 backdrop-blur-md px-4 py-4 md:px-6 md:py-5 border-b border-md-outline-variant/30 flex items-center gap-4 relative overflow-hidden sticky top-0 z-40">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-md-primary/10 rounded-full blur-[40px] -translate-y-1/2 translate-x-1/3"></div>
                  <div className="w-12 h-12 rounded-[14px] bg-md-surface-container-highest shadow-sm border border-md-outline-variant/30 flex items-center justify-center text-2xl flex-shrink-0 relative z-10">
                    {selectedCourse.icon}
                  </div>
                  <div className="flex flex-col relative z-10">
                    <h1 className="text-xl md:text-xl font-black text-md-on-surface tracking-tight leading-none mb-1">{selectedCourse.title}</h1>
                    <p className="text-md-on-surface-variant text-xs md:text-[13px] font-medium leading-snug truncate max-w-[250px] sm:max-w-md">{selectedCourse.description}</p>
                  </div>
                </div>
              )}

              <div className={`p-4 md:px-8 max-w-3xl mx-auto w-full flex-1 mt-0`}>
                


                {indexErrorUrl ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
                    <div className="w-16 h-16 bg-md-error-container text-md-on-error-container rounded-full flex items-center justify-center text-2xl mb-4">⚙️</div>
                    <h3 className="font-bold text-md-on-surface mb-2">Database Indexing Required</h3>
                    <p className="text-sm text-md-on-surface-variant mb-6 max-w-sm">This channel requires a Firestore index. Administrator action needed.</p>
                    <a href={indexErrorUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-md-error text-md-on-error px-6 py-3 rounded-xl font-bold text-sm shadow-md hover:opacity-90 transition-opacity">🔗 Create Index Now</a>
                  </div>
                ) : filteredMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 opacity-50 px-4 text-center animate-fade-in">
                    <div className="text-6xl mb-4 opacity-80 mix-blend-luminosity">📭</div>
                    <p className="font-bold text-xl text-md-on-surface">It's quiet here.</p>
                    <p className="text-sm mt-2 text-md-on-surface-variant max-w-xs">Be the brave first soul to start a discussion.</p>
                  </div>
                ) : !activeThreadId ? (
                  // Feed View (List of Thread Cards)
                  <div className="space-y-4">
                    {threads.map((thread) => {
                      const replies = repliesByThread[thread.id] || [];
                      const isUserOnline = allPresence[thread.userId];
                      const moodObj = (thread as any).mood ? MOODS.find(m => m.id === (thread as any).mood) : null;

                      return (
                        <div key={thread.id} onClick={() => handleViewThread(thread.id)} className={`${thread.isPinned ? "bg-amber-500/5 hover:bg-amber-500/10 border-amber-500/30 dark:bg-amber-500/10 dark:hover:bg-amber-500/20" : "bg-md-surface border-md-outline-variant/30 hover:border-md-primary/30"} p-5 md:p-6 rounded-[24px] border shadow-sm cursor-pointer hover:shadow-md transition-all group/thread animate-fade-in relative overflow-hidden flex flex-col gap-3`}>
                          <div className={`absolute top-0 left-0 w-1.5 h-full opacity-0 group-hover/thread:opacity-100 transition-opacity rounded-l-[24px] ${thread.isPinned ? "bg-amber-500" : "bg-md-primary"}`}></div>
                          
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3">
                               <div className="relative flex-shrink-0">
                                  <div className="w-10 h-10 rounded-full bg-md-surface-container-high border border-md-outline-variant flex items-center justify-center font-bold text-md-on-surface text-sm shadow-sm ring-2 ring-md-surface">
                                    {thread.userName.charAt(0).toUpperCase()}
                                  </div>
                                  {isUserOnline && (
                                    <div className="absolute -bottom-[2px] -right-[2px] w-3 h-3 bg-emerald-500 rounded-full border-2 border-md-surface shadow-sm"></div>
                                  )}
                               </div>
                               <div className="flex flex-col">
                                 <div className="flex items-center gap-2">
                                   <h4 className="font-bold text-md-on-surface text-sm">@{thread.userName}</h4>
                                 </div>
                                 <div className="flex items-center gap-2">
                                   <span className="text-[11px] text-md-on-surface-variant font-medium mt-0.5">{formatTime(thread.createdAt)}</span>
                                   {thread.isPinned && (
                                      <div className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-600 px-1.5 py-0.5 rounded-sm font-bold mt-0.5 ml-1"><span className="text-[9px]">📌</span> PINNED</div>
                                   )}
                                   <div className="relative">
                                     <button onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === thread.id ? null : thread.id); }} className="px-1 py-0.5 -mr-1 hover:bg-md-outline-variant/10 rounded-full transition-colors text-md-on-surface-variant relative z-20 leading-none pb-1.5 font-bold">⋮</button>
                                     {activeDropdown === thread.id && (
                                       <div className="absolute top-0 right-full pr-2 w-36 z-[100]" onClick={(e) => e.stopPropagation()}>
                                         <div className="bg-md-surface border border-md-outline-variant/30 rounded-xl shadow-lg overflow-hidden text-sm animate-fade-in text-md-on-surface">
                                           {isStaff && (
                                              <button onClick={() => { setActiveDropdown(null); handlePinThread(thread.id, !!thread.isPinned); }} className="w-full text-left px-4 py-2 hover:bg-amber-500/10 hover:text-amber-600 font-semibold transition-colors border-b border-md-outline-variant/20">{thread.isPinned ? "Unpin Thread" : "Pin Thread"}</button>
                                           )}
                                           <button onClick={() => { setActiveDropdown(null); handleShare(thread.id, thread.title || thread.text); }} className="w-full text-left px-4 py-2 hover:bg-md-primary/10 hover:text-md-primary font-semibold transition-colors border-b border-md-outline-variant/20">Share</button>
                                           {replies.length === 0 && thread.userId === currentUser?.uid && (
                                             <button onClick={() => openEditModal(thread as any)} className="w-full text-left px-4 py-2 hover:bg-md-primary/10 hover:text-md-primary font-semibold transition-colors border-b border-md-outline-variant/20">Edit</button>
                                           )}
                                           {thread.userId === currentUser?.uid && (
                                             <button onClick={() => { setActiveDropdown(null); setDeleteConfirm({ id: thread.id, isThread: true }); }} className="w-full text-left px-4 py-2 hover:bg-md-error-container hover:text-md-error font-semibold transition-colors">Delete</button>
                                           )}
                                           {thread.userId !== currentUser?.uid && (
                                             <button onClick={() => { setActiveDropdown(null); setReportPost(thread as any); }} className="w-full text-left px-4 py-2 hover:bg-md-error-container hover:text-md-error font-semibold transition-colors">Report Post</button>
                                           )}
                                         </div>
                                       </div>
                                     )}
                                   </div>
                                 </div>
                               </div>
                            </div>
                            
                            {moodObj && (
                               <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${moodObj.color} shadow-sm mix-blend-multiply`}>
                                 <span className="text-[10px]">{moodObj.emoji}</span>
                                 <span className="text-[10px] font-black uppercase tracking-wider">{moodObj.label}</span>
                               </div>
                            )}
                          </div>
                          
                          <div className="mt-1 pl-1">
                            {thread.title && (
                              <h3 className="font-black text-xl text-md-on-surface mb-2 tracking-tight group-hover/thread:text-md-primary transition-colors">{thread.title}</h3>
                            )}
                            <p className="text-md-on-surface-variant text-[15px] leading-relaxed line-clamp-4 mb-4">
                              <FormattedText text={thread.text} />
                            </p>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-auto pl-1 border-t border-md-outline-variant/20 pt-4 gap-4">
                            <div className="flex items-center gap-6 flex-wrap">
                               <div className="flex items-center gap-4 text-xs font-bold text-md-on-surface-variant transition-colors">
                                  <span className="flex items-center gap-1.5 hover:text-md-primary group-hover/thread:text-md-primary cursor-pointer" onClick={(e) => { e.stopPropagation(); handleViewThread(thread.id); }}>
                                     <span className="text-lg opacity-70">💬</span> {replies.length}
                                  </span>
                                  <span className="flex items-center gap-1.5 opacity-80 cursor-help" title="Total Views">
                                     <span className="text-lg opacity-70">👀</span> {thread.views || 0}
                                  </span>
                               </div>
                               <ReactionBar message={thread as ChatMessage} onReact={handleReaction} uid={currentUser?.uid || ''} className="mt-0" />
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              {activeQuickReply !== thread.id && (
                                <button onClick={(e) => { e.stopPropagation(); setActiveQuickReply(thread.id); setQuickReplyText(prev => ({...prev, [thread.id]: ''})); }} className="text-xs font-bold text-md-on-surface-variant hover:text-md-primary transition-colors flex items-center gap-1.5 px-3 py-1.5 bg-md-surface-variant/50 hover:bg-md-primary/10 rounded-full">
                                   <span>↩️</span> Quick Reply
                                </button>
                              )}
                              <div className="flex items-center text-xs font-bold text-md-primary opacity-0 group-hover/thread:opacity-100 -translate-x-2 group-hover/thread:translate-x-0 transition-all cursor-pointer" onClick={(e) => { e.stopPropagation(); handleViewThread(thread.id); }}>
                                 Open Thread <span className="ml-1">→</span>
                              </div>
                            </div>
                          </div>

                          {/* Quick Reply Drawer */}
                          {activeQuickReply === thread.id && (
                             <div className="mt-4 pt-4 border-t border-md-outline-variant/20 animate-slide-up" onClick={e => e.stopPropagation()}>
                                <form onSubmit={(e) => handleQuickReply(e, thread.id)} className="flex items-end gap-3 bg-md-surface-container-low p-2 rounded-[20px] focus-within:ring-2 focus-within:ring-md-primary/50 transition-all shadow-sm">
                                   <div className="w-8 h-8 shrink-0 rounded-full bg-md-primary text-md-on-primary flex items-center justify-center font-bold text-xs shadow-sm ml-1 mb-1">
                                      {(userProfile?.nickname || userProfile?.displayName || 'U').charAt(0).toUpperCase()}
                                   </div>
                                   <div className="flex-1 relative">
                                      <textarea
                                         value={quickReplyText[thread.id] || ''}
                                         onChange={e => setQuickReplyText(prev => ({...prev, [thread.id]: e.target.value}))}
                                         placeholder="Write a quick supportive reply..."
                                         className="w-full bg-transparent border-none focus:ring-0 text-sm py-2.5 max-h-32 min-h-[44px] resize-none overflow-y-auto custom-scrollbar"
                                         onKeyDown={e => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickReply(e, thread.id); } }}
                                         autoFocus
                                      />
                                   </div>
                                   <div className="flex items-center gap-2 mb-1.5 mr-1">
                                      <button type="button" onClick={() => setActiveQuickReply(null)} className="p-2 text-md-on-surface-variant hover:text-md-error hover:bg-md-error/10 rounded-full transition-colors flex items-center justify-center">
                                         ✕
                                      </button>
                                      <button type="submit" disabled={!(quickReplyText[thread.id]?.trim()) || isQuickReplying[thread.id]} className="w-8 h-8 bg-md-primary text-md-on-primary rounded-full hover:shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:hover:shadow-none flex items-center justify-center">
                                         {isQuickReplying[thread.id] ? <span className="text-[10px]">⏳</span> : <span className="text-[14px]">↑</span>}
                                      </button>
                                   </div>
                                </form>
                             </div>
                          )}

                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Detailed Thread View
                  <div className="space-y-8 animate-fade-in relative pb-10">
                    <button onClick={() => setActiveThreadId(null)} className="flex items-center gap-2 text-[11px] font-bold text-md-on-surface-variant hover:text-md-primary bg-md-surface border border-md-outline-variant/40 px-4 py-2 rounded-full transition-all shadow-sm w-fit mb-6 uppercase tracking-wider active:scale-95">
                      <span>←</span> Back to Feed
                    </button>
                    
                    {activeThread && (
                       <div className={`${activeThread.isPinned ? "bg-amber-500/5 border-amber-500/30 dark:bg-amber-500/10" : "bg-md-surface border-md-outline-variant/40"} p-6 md:p-8 rounded-[32px] border shadow-sm relative overflow-hidden transition-colors`}>
                          <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-[40px] -translate-y-1/2 translate-x-1/2 ${activeThread.isPinned ? "bg-amber-500/10" : "bg-md-primary/5"}`}></div>
                          <div className="flex items-center justify-between gap-4 mb-6 relative z-50">
                             <div className="flex items-center gap-4">
                               <div className="relative flex-shrink-0 cursor-pointer hover:scale-105 transition-transform" onClick={() => setViewingProfile({ name: activeThread.userName, uid: activeThread.userId, role: 'parent', isTeam: false })}>
                                  <div className="w-12 h-12 rounded-full bg-md-primary text-md-on-primary flex items-center justify-center font-bold text-lg shadow-md ring-4 ring-md-primary/20">
                                    {activeThread.userName.charAt(0).toUpperCase()}
                                  </div>
                                  {allPresence[activeThread.userId] && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-md-surface shadow-sm"></div>
                                  )}
                               </div>
                               <div>
                                 <h4 className="font-bold text-md-on-surface text-base cursor-pointer hover:text-md-primary" onClick={() => setViewingProfile({ name: activeThread.userName, uid: activeThread.userId, role: 'parent', isTeam: false })}>@{activeThread.userName}</h4>
                                 <div className="flex items-center gap-2 mt-0.5">
                                   <span className="text-xs text-md-on-surface-variant font-medium">{formatTime(activeThread.createdAt)}</span>
                                   <span className="flex items-center gap-1 text-[10px] font-bold text-md-on-surface-variant bg-md-surface-variant/30 px-1.5 py-0.5 rounded-sm"><span className="text-[10px] opacity-80">👀</span> {activeThread.views || 0}</span>
                                   {activeThread.isPinned && (
                                      <div className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-600 px-1.5 py-0.5 rounded-sm font-bold"><span className="text-[9px]">📌</span> PINNED</div>
                                   )}
                                 </div>
                               </div>
                             </div>
                             {(activeThread as any).mood && (() => {
                                const mood = MOODS.find(m => m.id === (activeThread as any).mood);
                                return mood ? (
                                   <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${mood.color} drop-shadow-sm`}>
                                     <span className="text-sm">{mood.emoji}</span>
                                     <span className="text-[10px] font-black uppercase tracking-wider">{mood.label}</span>
                                   </div>
                                ) : null;
                             })()}
                               <div className="relative ml-2">
                                 <button onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === activeThread.id ? null : activeThread.id); }} className="p-1 px-2 -mr-2 hover:bg-md-outline-variant/10 rounded-full transition-colors text-md-on-surface-variant font-bold text-xl leading-none">⋮</button>
                                 {activeDropdown === activeThread.id && (
                                   <div className="absolute top-0 right-full pr-2 w-36 z-[60]" onClick={(e) => e.stopPropagation()}>
                                     <div className="bg-md-surface border border-md-outline-variant/30 rounded-xl shadow-lg overflow-hidden text-sm animate-fade-in text-md-on-surface">
                                       {isStaff && (
                                          <button onClick={() => { setActiveDropdown(null); handlePinThread(activeThread.id, !!activeThread.isPinned); }} className="w-full text-left px-4 py-2.5 hover:bg-amber-500/10 hover:text-amber-600 font-semibold transition-colors border-b border-md-outline-variant/20">{activeThread.isPinned ? "Unpin Thread" : "Pin Thread"}</button>
                                       )}
                                       <button onClick={() => { setActiveDropdown(null); handleShare(activeThread.id, activeThread.title || activeThread.text); }} className="w-full text-left px-4 py-2.5 hover:bg-md-primary/10 hover:text-md-primary font-semibold transition-colors border-b border-md-outline-variant/20">Share</button>
                                       {(repliesByThread[activeThreadId] || []).length === 0 && activeThread.userId === currentUser?.uid && (
                                         <button onClick={() => openEditModal(activeThread as any)} className="w-full text-left px-4 py-2.5 hover:bg-md-primary/10 hover:text-md-primary font-semibold transition-colors border-b border-md-outline-variant/20">Edit</button>
                                       )}
                                       {activeThread.userId === currentUser?.uid && (
                                         <button onClick={() => { setActiveDropdown(null); setDeleteConfirm({ id: activeThread.id, isThread: true }); }} className="w-full text-left px-4 py-2.5 hover:bg-md-error-container hover:text-md-error font-semibold transition-colors">Delete</button>
                                       )}
                                       {activeThread.userId !== currentUser?.uid && (
                                         <button onClick={() => { setActiveDropdown(null); setReportPost(activeThread as any); }} className="w-full text-left px-4 py-2.5 hover:bg-md-error-container hover:text-md-error font-semibold transition-colors">Report Post</button>
                                       )}
                                     </div>
                                   </div>
                                 )}
                               </div>
                          </div>
                          {activeThread.title && (
                            <h2 className="font-black text-2xl md:text-3xl text-md-on-surface mb-4 leading-tight tracking-tight relative z-10">{activeThread.title}</h2>
                          )}
                          <p className="text-md-on-surface text-base md:text-[17px] leading-relaxed whitespace-pre-wrap relative z-10"><FormattedText text={activeThread.text} /></p>
                          <div className="relative z-10 pb-4">
                            <ReactionBar message={activeThread} onReact={handleReaction} uid={currentUser?.uid || ''} />
                          </div>
                       </div>
                    )}

                    <div className="md:pl-10 space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <h4 className="font-black text-lg text-md-on-surface">Responses</h4>
                        <div className="h-px bg-md-outline-variant/30 flex-1"></div>
                      </div>
                      {(repliesByThread[activeThreadId] || []).length === 0 ? (
                        <p className="text-sm text-md-on-surface-variant italic pl-2 border-l-2 border-md-outline-variant/30 py-1">No responses yet. Add yours below.</p>
                      ) : (
                        (repliesByThread[activeThreadId] || []).map((reply) => {
                           const isUserOnline = allPresence[reply.userId];
                           return (
                             <div key={reply.id} className="group/post flex gap-4 md:gap-5 animate-fade-in relative items-start">
                                <div className="absolute left-[19px] top-10 bottom-[-24px] w-0.5 bg-md-outline-variant/20 last:hidden"></div>
                                <div className="relative flex-shrink-0 z-10 mt-1">
                                  <div className="w-10 h-10 rounded-full bg-md-surface-container-high border border-md-outline-variant/30 flex items-center justify-center font-bold text-md-on-surface text-sm cursor-pointer shadow-sm hover:ring-2 hover:ring-md-primary/30 transition-all" onClick={() => setViewingProfile({ name: reply.userName, uid: reply.userId, role: 'parent', isTeam: false })}>
                                    {reply.userName.charAt(0).toUpperCase()}
                                  </div>
                                  {isUserOnline && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-md-surface-container-lowest"></div>
                                  )}
                                </div>
                                <div className="flex-1 space-y-2 pb-2">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <div className="flex items-baseline gap-2">
                                      <h4 className="font-bold text-md-on-surface text-sm cursor-pointer hover:text-md-primary" onClick={() => setViewingProfile({ name: reply.userName, uid: reply.userId, role: 'parent', isTeam: false })}>@{reply.userName}</h4>
                                      <span className="text-[10px] text-md-on-surface-variant font-medium">• {formatTime(reply.createdAt)}</span>
                                    </div>
                                      <div className="relative">
                                        <button onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === reply.id ? null : reply.id); }} className="px-2 py-0.5 hover:bg-md-outline-variant/10 rounded-full transition-colors text-md-on-surface-variant text-sm border border-transparent font-bold">⋮</button>
                                        {activeDropdown === reply.id && (
                                          <div className="absolute top-0 right-full pr-2 w-32 z-50" onClick={(e) => e.stopPropagation()}>
                                            <div className="bg-md-surface border border-md-outline-variant/30 rounded-xl shadow-lg overflow-hidden animate-fade-in text-md-on-surface">
                                              <button onClick={() => { setActiveDropdown(null); handleShare(reply.parentId || reply.id); }} className="w-full text-left px-3 py-2 hover:bg-md-primary/10 hover:text-md-primary text-xs font-semibold transition-colors border-b border-md-outline-variant/20">Share</button>
                                              {reply.userId === currentUser?.uid && (
                                                <button onClick={() => { setActiveDropdown(null); setDeleteConfirm({ id: reply.id, isThread: false }); }} className="w-full text-left px-3 py-2 hover:bg-md-error-container hover:text-md-error text-xs font-semibold transition-colors border-b border-md-outline-variant/20">Delete</button>
                                              )}
                                              {reply.userId !== currentUser?.uid && (
                                                <button onClick={() => { setActiveDropdown(null); setReportPost(reply as any); }} className="w-full text-left px-3 py-2 hover:bg-md-error-container hover:text-md-error text-xs font-semibold transition-colors">Report</button>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                  </div>
                                  <div className="text-md-on-surface text-[15px] leading-relaxed whitespace-pre-wrap bg-md-surface px-5 py-4 rounded-[20px] rounded-tl-[4px] border border-md-outline-variant/40 shadow-sm inline-block min-w-[120px] max-w-full">
                                    <FormattedText text={reply.text} />
                                  </div>
                                  <ReactionBar message={reply} onReact={handleReaction} uid={currentUser?.uid || ''} />
                                  <div className="flex gap-2 pl-1">
                                    <button onClick={() => setReportPost(reply)} className="opacity-0 group-hover/post:opacity-100 transition-opacity text-[10px] font-bold text-md-on-surface-variant hover:text-md-error uppercase tracking-widest px-2 py-1 rounded-md">Report</button>
                                  </div>
                                </div>
                             </div>
                           )
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Universal Sticky Input Bar */}
            {!indexErrorUrl && (
              <div className="flex-shrink-0 p-4 border-t border-md-outline-variant/30 bg-md-surface-container-highest z-20">
                <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-3 relative overflow-visible animate-fade-in">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-md-primary text-md-on-primary items-center justify-center font-bold text-sm shadow-md mt-1 hidden sm:flex">
                    {userProfile?.nickname?.charAt(0).toUpperCase() || userProfile?.displayName?.charAt(0).toUpperCase() || 'D'}
                  </div>
                  <div className="flex-1 flex flex-col gap-2 relative">
                    <textarea
                      value={newMessage}
                      onChange={handleMessageChange}
                      placeholder={activeThreadId ? `Reply to @${activeThread?.userName}...` : "What's happening? Type @ to tag a fellow Dad..."}
                      disabled={isSending}
                      rows={Math.max(1, Math.min(6, newMessage.split('\n').length))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e as any);
                        }
                      }}
                      className="w-full bg-md-surface px-5 py-3.5 rounded-[20px] resize-none text-md-on-surface text-sm md:text-base placeholder-md-on-surface-variant/70 outline-none focus:ring-2 focus:ring-md-primary/30 transition-all border border-md-outline-variant/40 shadow-sm leading-relaxed overflow-y-auto max-h-[150px] custom-scrollbar"
                    />

                    {/* Mention Dropdown */}
                    {mentionSearch?.active && mentionMatches.length > 0 && (
                      <div className="absolute bottom-full left-0 mb-2 w-64 max-h-[160px] overflow-y-auto bg-md-surface-container-high border border-md-outline-variant/30 rounded-2xl shadow-2xl z-[200] text-xs custom-scrollbar">
                        {mentionMatches.map(name => (
                          <button
                            type="button"
                            key={name}
                            className="w-full text-left px-5 py-3 hover:bg-md-primary/10 hover:text-md-primary font-bold text-md-on-surface transition-colors border-b border-md-outline-variant/10 last:border-0"
                            onClick={() => insertMention(name)}
                          >
                            @{name}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center px-2">
                      <p className="text-[10px] text-md-on-surface-variant font-medium flex items-center gap-1.5 hidden sm:flex">
                        <span className="text-md-primary">🛡️</span> Child-focused supportive advice only.
                      </p>
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || isSending}
                        className="px-6 py-2 ml-auto !rounded-full bg-md-primary text-md-on-primary font-bold text-sm shadow-md hover:opacity-90 disabled:opacity-30 disabled:shadow-none transition-all active:scale-95 flex items-center gap-2"
                      >
                        {isSending ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : (activeThreadId ? 'Reply' : 'Post')}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>

          <aside className={`
            fixed inset-y-0 right-0 z-[60] w-72 bg-md-surface transform transition-transform duration-300 border-l border-md-outline-variant/30 shadow-2xl flex flex-col h-full
            md:relative md:translate-x-0 md:z-auto md:shadow-none flex-shrink-0
            ${showUsersMobile ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
          `}>
            <div className="p-5 flex-shrink-0 flex justify-between items-center bg-md-surface-container border-b border-md-outline-variant/30">
              <h3 className="text-[10px] font-black text-md-on-surface uppercase tracking-[0.2em] flex items-center gap-2 drop-shadow-sm">
                <span>📜</span> Community Area
              </h3>
              <button onClick={() => setShowUsersMobile(false)} className="md:hidden text-md-on-surface-variant hover:text-md-on-surface p-1">✕</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5 space-y-6 custom-scrollbar">
               {/* Community Guidelines */}
               <div className="bg-md-primary-container text-md-on-primary-container p-5 rounded-3xl shadow-sm border border-md-primary/10">
                  <h4 className="font-black text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                    <span>⚖️</span> Golden Rules
                  </h4>
                  <ul className="space-y-4 text-xs font-medium">
                     <li className="flex gap-2">
                       <span className="text-md-primary font-bold">1.</span>
                       <span className="leading-relaxed"><strong>Child Paramountcy:</strong> Discussions must center on what is best for the child, not just parental rights.</span>
                     </li>
                     <li className="flex gap-2">
                       <span className="text-md-primary font-bold">2.</span>
                       <span className="leading-relaxed"><strong>No Mud-Slinging:</strong> Venting is fine in appropriate channels, but avoid defamatory attacks.</span>
                     </li>
                     <li className="flex gap-2">
                       <span className="text-md-primary font-bold">3.</span>
                       <span className="leading-relaxed"><strong>We Are Peers:</strong> Advice here is supportive, not a substitute for formal legal counsel.</span>
                     </li>
                  </ul>
               </div>

               {/* Quick Links */}
               <div>
                  <h4 className="font-bold text-xs uppercase tracking-widest text-md-on-surface-variant mb-3 px-1">Quick Links</h4>
                  <div className="space-y-2">
                    <button onClick={() => {
                        const link = document.createElement('a');
                        link.href = '/Position_Statement_Template.docx';
                        link.download = 'Position_Statement_Template.docx';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }} className="w-full text-left px-4 py-2.5 rounded-xl bg-md-surface-container-low hover:bg-md-surface-container text-sm font-bold text-md-on-surface transition-colors border border-md-outline-variant/20 shadow-sm flex justify-between items-center">
                      <span>📄 Blank Position Statement</span> <span>↓</span>
                    </button>
                    <button onClick={() => {
                        const link = document.createElement('a');
                        link.href = '/Communication_Template.docx';
                        link.download = 'Communication_Template.docx';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }} className="w-full text-left px-4 py-2.5 rounded-xl bg-md-surface-container-low hover:bg-md-surface-container text-sm font-bold text-md-on-surface transition-colors border border-md-outline-variant/20 shadow-sm flex justify-between items-center">
                      <span>🗣️ Communication Template</span> <span>↓</span>
                    </button>
                    <button onClick={() => setShowThermometer(true)} className="w-full text-left px-4 py-2.5 rounded-xl bg-md-surface-container-low hover:bg-md-surface-container text-sm font-bold text-md-on-surface transition-colors border border-md-outline-variant/20 shadow-sm flex justify-between items-center">
                      <span>🧘 Check Your Health</span> <span>→</span>
                    </button>
                  </div>
               </div>

               {/* Online Quick Stats */}
               <div>
                  <h4 className="font-bold text-xs uppercase tracking-widest text-md-on-surface-variant mb-3 px-1">Activity Loop</h4>
                  <div className="flex items-center gap-3 px-4 py-3 bg-md-surface-container-low rounded-2xl border border-md-outline-variant/20 shadow-sm cursor-pointer hover:bg-md-surface-container" onClick={() => setShowUsersMobile(false)}>
                    <div className="relative">
                      <span className="text-2xl drop-shadow-sm">👨‍👧</span>
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-md-surface-container-low shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-md-on-surface">{Object.keys(allPresence).length} Peers Online</p>
                      <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest">Active Now</p>
                    </div>
                  </div>
               </div>
            </div>
            
            {/* Mobile Post Button */}
            <div className="p-4 border-t border-md-outline-variant/30 bg-md-surface-container md:hidden">
              <Button 
                onClick={() => { setActiveThreadId(null); setIsCreatingThread(true); setShowUsersMobile(false); setShowTopicsMobile(false); }}
                className="w-full !rounded-2xl py-3.5 shadow-md flex items-center justify-center gap-2"
              >
                <span className="text-lg">✍️</span> New Post
              </Button>
            </div>
          </aside>
        </div>
      </div>

      {(showTopicsMobile || showUsersMobile) && (
        <div
          className="fixed inset-0 bg-md-scrim/60 backdrop-blur-sm z-50 md:hidden"
          onClick={() => { setShowTopicsMobile(false); setShowUsersMobile(false); }}
        />
      )}

      {/* Floating Modal - we'll keep it here but we can optionally eliminate it entirely since the inline form does everything now. In this case, we'll remove it or conditionally empty it. */}
      {/* We removed the full Create Thread Modal to implement Twitter inline style */}

      {viewingProfile && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4 z-[100] animate-fade-in">
          <Card className="max-w-md w-full !rounded-3xl p-8 shadow-2xl bg-white relative overflow-hidden">
            <button onClick={() => setViewingProfile(null)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-900 text-xl">✕</button>
            <div className="text-center mb-8">
              <div className="relative w-20 h-20 mx-auto mb-4">
                <div className="w-20 h-20 bg-indigo-50 border border-indigo-100 rounded-3xl flex items-center justify-center text-3xl font-bold text-indigo-600 shadow-sm">
                  {viewingProfile.name.charAt(0).toUpperCase()}
                </div>
                {allPresence[viewingProfile.uid] && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white shadow-lg animate-pulse"></div>
                )}
              </div>
              <h3 className="text-xl font-heading font-bold text-slate-900">@{viewingProfile.name}</h3>
              <p className="text-xs text-slate-500 mt-1">Dads Hub Member</p>
              {viewingProfile.isTeam && <Badge color="blue">Dads Matter Team</Badge>}
              {allPresence[viewingProfile.uid] && (
                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mt-2">Active Online Now</p>
              )}
            </div>

            <div className="space-y-4">
              {userProfile?.uid === viewingProfile.uid ? (
                <div className="text-center p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">This is your profile</p>
                </div>
              ) : connectionStatus === 'accepted' ? (
                <Button onClick={() => navigate('/messages')} className="w-full !py-4 font-black uppercase tracking-widest shadow-xl">Go to Messages</Button>
              ) : connectionStatus === 'pending' ? (
                <div className="space-y-4">
                  <div className="text-center p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Connection Request Pending</p>
                  </div>
                  {isAdmin && (
                    <Button onClick={handleRequestConnection} disabled={isConnecting} className="w-full !py-4 font-black uppercase tracking-widest shadow-xl !bg-rose-600">
                      {isConnecting ? "Redirecting..." : "Direct Message (Admin Override)"}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                      {isAdmin
                        ? "As an Admin, you can message this user directly to address violations of service."
                        : "Connect with this member to enable private messaging and peer support. You both must accept before a chat begins."}
                    </p>
                  </div>
                  <Button onClick={handleRequestConnection} disabled={isConnecting} className={`w-full !py-4 font-black uppercase tracking-widest shadow-xl ${isAdmin ? '!bg-rose-600' : ''}`}>
                    {isConnecting ? "Processing..." : (isAdmin ? "Direct Message (Admin)" : "Request Connection")}
                  </Button>
                </div>
              )}
            </div>

            {!isPaid && !isAdmin && (
              <p className="text-[10px] text-rose-500 text-center mt-6 font-bold uppercase tracking-tighter">🔒 Parent PRO Membership required for peer messaging</p>
            )}
          </Card>
        </div>
      )}

      {reportPost && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4 z-[200] animate-fade-in">
          <Card className="max-w-md w-full !rounded-3xl p-8 shadow-2xl border-0 ring-1 ring-white/10 bg-white">
            <div className="text-center mb-8">
              <span className="text-4xl block mb-3">🚩</span>
              <h3 className="text-xl font-heading font-bold text-slate-900">Report Message</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-[250px] mx-auto">Help us keep the Dads Hub a safe and supportive space.</p>
            </div>
            <form onSubmit={handleReportSubmit} className="space-y-3">
              {['Toxicity / Aggression', 'Dangerous Legal Advice', 'Personal Information Sharing'].map(reason => (
                <label key={reason} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all cursor-pointer group ${reportReason === reason ? 'border-indigo-600 bg-indigo-50' : 'border-slate-100 bg-slate-50 hover:bg-slate-100'}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${reportReason === reason ? 'border-indigo-600' : 'border-slate-300'}`}>
                    {reportReason === reason && <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />}
                  </div>
                  <input
                    type="radio"
                    name="report_reason"
                    value={reason}
                    checked={reportReason === reason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="hidden"
                  />
                  <span className={`text-xs font-bold ${reportReason === reason ? 'text-indigo-900' : 'text-slate-600'}`}>{reason}</span>
                </label>
              ))}
              <div className="flex gap-3 pt-6">
                <Button variant="ghost" type="button" onClick={() => setReportPost(null)} className="flex-1 !py-3 text-xs">Cancel</Button>
                <Button variant="danger" type="submit" disabled={!reportReason || isSubmittingReport} className="flex-1 !py-3 text-xs uppercase tracking-widest font-black">
                  {isSubmittingReport ? 'Sending...' : 'Report'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4 z-[200] animate-fade-in">
          <Card className="max-w-md w-full !rounded-3xl p-8 shadow-2xl border-0 ring-1 ring-white/10 bg-white">
            <div className="text-center mb-8">
              <span className="text-4xl block mb-3">🗑️</span>
              <h3 className="text-xl font-heading font-bold text-slate-900">Delete {deleteConfirm.isThread ? 'Discussion' : 'Reply'}?</h3>
              <p className="text-xs text-slate-500 mt-2 max-w-[250px] mx-auto">
                {deleteConfirm.isThread 
                  ? "This action will permanently delete your discussion and all of its replies." 
                  : "This action will permanently delete your reply."}
              </p>
            </div>
            <div className="flex gap-3 pt-6">
              <Button variant="ghost" type="button" onClick={() => setDeleteConfirm(null)} className="flex-1 !py-3 text-xs">Cancel</Button>
              <Button variant="danger" type="button" onClick={handleDeletePost} disabled={isDeleting} className="flex-1 !py-3 text-xs uppercase tracking-widest font-black">
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {editingThread && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4 z-[200] animate-fade-in">
          <Card className="max-w-md w-full !rounded-3xl p-8 shadow-2xl border-0 ring-1 ring-white/10 bg-white relative">
            <button onClick={() => setEditingThread(null)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-900 text-xl">✕</button>
            <div className="mb-6">
              <h3 className="text-xl font-heading font-bold text-slate-900">Edit Discussion</h3>
            </div>
            <div className="space-y-4">
              {editingThread.title && (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Thread Title"
                  className="w-full bg-slate-50 px-5 py-3 rounded-[20px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all border border-slate-200/60 font-bold"
                />
              )}
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Message content..."
                rows={5}
                className="w-full bg-slate-50 px-5 py-4 rounded-[20px] resize-y text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all border border-slate-200/60 custom-scrollbar"
              />
              <div className="flex gap-3 pt-2">
                <Button variant="ghost" type="button" onClick={() => setEditingThread(null)} className="flex-1 !py-3 text-xs">Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={!editText.trim() || isSavingEdit} className="flex-1 !py-3 text-xs uppercase tracking-widest font-black">
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {showThermometer && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-md-on-surface/40 backdrop-blur-sm">
               <SystemCheckFlow 
                   onComplete={handleSystemCheckComplete}
                   onCancel={() => setShowThermometer(false)}
               />
          </div>
      )}

      {showIntervention && latestCheckinData && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-md-on-surface/40 backdrop-blur-sm">
              <SystemDiagnostic 
                  data={latestCheckinData!} 
                  onClose={() => setShowIntervention(false)} 
              />
          </div>
      )}
    </Layout>
  );
};

export default Forum;
